'use client';

import React, { useState } from 'react';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { fetchTokenPriceData } from './geckoTerminalService';
import { env } from '@web/utils/env';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const TimeRangeContainer = styled.div`
  display: flex;
  margin-top: 2rem;
  margin-bottom: 1.5rem;
  justify-content: space-evenly;
`;

const TimeButton = styled.div<{ active: boolean; disabled?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  padding-block: ${({ theme }) => theme.spacing('0250')};
  padding-inline: ${({ theme }) => theme.spacing('0500')};
  background: ${({ theme, active }) =>
    active ? theme.color.onSilver : 'transparent'};
  border-radius: ${({ theme, active }) =>
    active ? theme.shape.corner['0250'] : '0'};

  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  text-transform: uppercase;
  color: ${({ theme, active }) =>
    active ? theme.color.silver : theme.color.onSilver};
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.onSilver};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  // Muted rather than red: the usual reason this shows is a transient rate
  // limit on a free public price API, which is not something to alarm a
  // visitor about.
  color: ${({ theme }) => theme.color.onSilver};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
`;

const ChartWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 8.75rem;
  box-sizing: border-box;
`;

const ToolTipContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${({ theme }) => theme.spacing('0750')};
  gap: ${({ theme }) => theme.spacing('0250')};
  background: ${({ theme }) => theme.color.dark};
  border-radius: ${({ theme }) => theme.shape.corner['0500']};
`;

const ToolTipPrice = styled.span`
  color: ${({ theme }) => theme.color.light};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  text-transform: uppercase;
`;

const ToolTipDate = styled.span`
  color: ${({ theme }) => theme.color.light};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0680']};
  opacity: ${({ theme }) => theme.opacity.solid};
  line-height: ${({ theme }) => theme.typescale.lineHeight.default};
  text-transform: uppercase;
`;

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    const date = new Date(label);
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    return (
      <ToolTipContainer>
        <ToolTipPrice>${value.toFixed(4)}</ToolTipPrice>
        <ToolTipDate>{formattedDate.toUpperCase()}</ToolTipDate>
      </ToolTipContainer>
    );
  }
  return null;
};

export const UnaChart = () => {
  const theme = useTheme();
  const [activeTimeRange, setActiveTimeRange] = useState('1M');

  const {
    data = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['una-chart-data', activeTimeRange],
    queryFn: () => fetchTokenPriceData(activeTimeRange),
    // GeckoTerminal rate-limits hard, and its 429 carries no CORS header — so
    // the browser reports a rate limit as a CORS failure and the chart reads
    // as broken. Polling every 30s made that near-certain. A public page needs
    // no live ticker: fetch once, hold it, and ride out a 429 with backoff.
    refetchInterval: env.SHELL_MODE ? false : 30_000,
    staleTime: env.SHELL_MODE ? 5 * 60_000 : 30_000,
    retry: 3,
    retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 8000),
  });

  const handleTimeRangeChange = (range: string) => {
    setActiveTimeRange(range);
  };

  const hasNoData = !isLoading && data.length === 0;

  const timeRanges = ['1H', 'DAY', 'WEEK', '1M', 'YEAR'];

  return (
    <Container>
      <ChartWrapper>
        {isLoading ? (
          <LoadingContainer>Loading chart data...</LoadingContainer>
        ) : error ? (
          <ErrorContainer>
            {error instanceof Error
              ? error.message
              : 'Failed to load chart data'}
          </ErrorContainer>
        ) : hasNoData ? (
          <ErrorContainer>No data available for this time range</ErrorContainer>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="100%"
                    stopColor={theme.color.blue}
                    stopOpacity={1}
                  />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="timestamp"
                type="number"
                domain={['dataMin', 'dataMax']}
                hide
              />
              <YAxis domain={['dataMin', 'dataMax']} hide />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{
                  stroke: theme.color.onSilver,
                  strokeWidth: 1,
                  strokeDasharray: '3 3',
                }}
              />
              <Area
                type="monotone"
                dataKey="price"
                stroke={theme.color.onSilver}
                strokeWidth={1}
                fillOpacity={1}
                fill="url(#colorPrice)"
                dot={false}
                activeDot={{
                  r: 8,
                  fill: theme.color.yellowFixed,
                  stroke: theme.color.onBackground,
                  strokeWidth: 1,
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartWrapper>

      <TimeRangeContainer>
        {timeRanges.map((range) => (
          <TimeButton
            key={range}
            active={activeTimeRange === range}
            disabled={isLoading}
            onClick={() => !isLoading && handleTimeRangeChange(range)}
          >
            {range}
          </TimeButton>
        ))}
      </TimeRangeContainer>
    </Container>
  );
};
